import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import useModalState from '@/hooks/pure/useModalState';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import IntegrationImportModal from '../../components/IntegrationImportModal';
import { ProviderContainer } from '../../components/ProviderContainer';
import type { IntegrationItemProps } from '../../types';
import { KnocommerceConnectModal } from './KnocommerceConnectModal';
import { useKnocommerce } from './useKnocommerce';

export const KnocommerceItem = ({ isConnector }: IntegrationItemProps) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const fetchFeedbackSources = useFeedbackSourceStore(
    (state) => state.fetchFeedbackSources,
  );
  const {
    knocommerceWorkspace,
    connectMutation,
    disconnectMutation,
    requestImportMutation,
  } = useKnocommerce(!isConnector);

  const connectModalState = useModalState();
  const importModalState = useModalState<{ isNew: boolean }>();

  const handleConnectComplete = () => {
    if (!currWorkspace) return;
    fetchFeedbackSources(currWorkspace.alias);
    connectModalState.close();
  };

  const handleImportWorkspace = (dateRange: NullableDateRangeT) => {
    requestImportMutation(dateRange);
    importModalState.close();
  };

  if (isConnector && knocommerceWorkspace) return null;

  return (
    <>
      <ProviderContainer
        multiInfoEnabled={false}
        connected={!!knocommerceWorkspace}
        source={SOURCE_TYPE.KNOCOMMERCE}
        description="Import feedback data from Knocommerce into Syncly."
        connectedDisplay={knocommerceWorkspace?.name || 'Knocommerce workspace'}
        info={knocommerceWorkspace!}
        onDisconnect={() => disconnectMutation()}
        onConnectClick={() => connectModalState.open()}
        additionalActions={() => (
          <div>
            <Button
              variant="outlined"
              onClick={() => importModalState.open({ modalState: { isNew: false } })}
            >
              One-time import
            </Button>
          </div>
        )}
      />
      <Modal
        status={connectModalState.status}
        closeModal={connectModalState.close}
        useBackgroundClose
      >
        <KnocommerceConnectModal
          onConnect={connectMutation}
          onConnectComplete={handleConnectComplete}
        />
      </Modal>
      <Modal
        status={importModalState.status}
        closeModal={importModalState.close}
        useBackgroundClose
      >
        {knocommerceWorkspace && (
          <IntegrationImportModal
            isNew={importModalState.modalState?.isNew || false}
            title="Import Knocommerce Feedback"
            subTitle="Import feedback data from Knocommerce into Syncly."
            historyList={knocommerceWorkspace.history || []}
            onSubmitImport={(start_date, end_date) =>
              handleImportWorkspace({ start_date, end_date })
            }
          />
        )}
      </Modal>
    </>
  );
};
